import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ContactService, ExtendedContact } from '../services/contact.service';
import {
  Contact,
  ContactFilterInput,
} from '../../generated/eveportal-api/graphql';
import {
  auditTime,
  debounceTime,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { SipService } from '../services/sip.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { TokenService } from '../services/token.service';

@Component({
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.css'],
})
export class ContactsComponent implements OnInit, OnDestroy {
  contacts: ExtendedContact[] = [];
  searchTerm = '';
  totalCount = 0;

  private readonly chunkSize = 50; // how many to fetch per API call
  private readonly preload = 20; // how many rows from end before prefetch
  loading = false;
  private hasMore = true;

  @ViewChild(CdkVirtualScrollViewport)
  private viewport!: CdkVirtualScrollViewport;

  private searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  scroll$ = new Subject<number>();

  private endUserOrganisationNode =
    this.tokenService.getJwtTokenPayload()?.EndUserOrganisationNode;
  trackByContactId = (_: number, c: ExtendedContact) => c.contactId;
  constructor(
    private contactService: ContactService,
    private sipService: SipService,
    private tokenService: TokenService,
    private snack: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$),
        switchMap((term) => this.loadContacts$(false, term))
      )
      .subscribe();

    // Trigger initial load
    this.searchTerm$.next('');

    // NEW: throttle drag events and serialize loads
    this.scroll$
      .pipe(
        auditTime(50), // ~20fps is plenty
        takeUntil(this.destroy$),
        filter(() => !this.loading && this.hasMore),
        filter(() => {
          const { end } = this.viewport.getRenderedRange();
          const dataLength = this.viewport.getDataLength();

          const nearBottom = dataLength - end <= this.preload;

          if (nearBottom) {
            return true;
          }

          return false;
        }),

        debounceTime(100), // wait for drag to pause
        // use switchMap to ignore intermediate triggers while one load is in flight
        exhaustMap(() => {
          this.loading = true;
          return this.loadContacts$(true, this.searchTerm).pipe(
            finalize(() => (this.loading = false))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  onSearch(): void {
    this.contacts = [];
    this.totalCount = 0;
    this.hasMore = true;
    setTimeout(() => {
      this.viewport?.scrollToIndex(0);
    }, 0);

    this.searchTerm$.next(this.searchTerm);
  }

  onScrolledIndexChange(index: number): void {
    this.scroll$.next(index);
  }

  private loadContacts$(append = false, term = this.searchTerm) {
    const skip = append ? this.contacts.length : 0;
    const take = this.chunkSize;
    const t = (this.searchTerm ?? '').trim();

    const scopeFilter: ContactFilterInput = {
      and: [
        {
          or: [
            { "number": { neq: null }},
            { "internalNumber": { neq: null }},
            { "phoneNumber": { neq: null }},
          ]
        },
        {
          or: [
            { organisationNode: { isDescendantOf: getCompanyNode(this.endUserOrganisationNode) } },
            { organisationNode: { eq: null } },
          ],
        }
      ]
     
    };

    const filter: ContactFilterInput = t
      ? {
          and: [
            scopeFilter,
            {
              or: [
                { firstName: { contains: t } },
                { surname: { contains: t } },
                { eveUserName: { contains: t } },
                { phoneNumber: { contains: t } },
              ],
            },
          ],
        }
      : scopeFilter;

    // ✅ capture current pixel offset BEFORE changing data
    const offsetBefore = this.viewport?.measureScrollOffset('top') ?? 0;

    return this.contactService.getContacts(filter, skip, take).pipe(
      tap((res) => {
        this.totalCount = res.totalCount ?? this.totalCount;

        const seen = new Set(this.contacts.map((c) => c.contactId));
        const incoming: ExtendedContact[] = (res.items ?? [])
          .filter((c) => !seen.has(c.contactId))
          .map((c) => ({ ...c, avatarUrl: 'assets/eveuser-avatar.png' }));

        // Apply data change, then restore pixel offset
        requestAnimationFrame(() => {
          this.contacts = append ? [...this.contacts, ...incoming] : incoming;

          // ✅ restore the exact pixel offset; no index math
          this.viewport?.scrollToOffset(offsetBefore, 'auto');
          this.viewport?.checkViewportSize();

          const received = incoming.length;
          this.hasMore = skip + received < this.totalCount;
        });
      }),
      switchMap(() => EMPTY)
    );
  }

  onAddContact() {
    console.log('Add contact clicked');
  }

  toggleFavourite(contact: Contact) {
    contact.favourite = !contact.favourite;
  }

  async onCall(contact: Contact) {
    try {
      console.log('Calling', contact);
      await this.sipService.makeCall(
        contact.phoneNumber ?? contact.eveUserName ?? ''
      );
    } catch (error: any) {
      const errorMessage =
        'Failed to make call: ' +
        (error instanceof Error ? error.message : 'Unknown error');
      this.snack.open(errorMessage, 'Dismiss');
    }
  }

  onChat(contact: Contact) {
    console.log('Chatting with', contact);
  }

  onVideoCall(contact: Contact) {
    console.log('Video calling', contact);
  }

  onDelete(contact: Contact) {
    console.log('Deleting', contact);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
export const getCompanyNode = (organisationNode: String | null): String | null => {
  if (organisationNode == null) return null;
  const organisationNodeParts = organisationNode.split("/");
  if (organisationNodeParts.length == 6) return organisationNode;

  return `/${organisationNodeParts[1]}/${organisationNodeParts[2]}/${organisationNodeParts[3]}/${organisationNodeParts[4]}/`;
}